// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type
// checks for this file, matching MonthlyBreakdownChart.tsx and LenderBarChart.tsx.
import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import type { LodgementRecord } from '../../hooks/useLodgements';
import {
  TARGET_TYPE_LABELS,
  type LodgementTargetType,
  type TargetProgress,
} from '../../hooks/useLodgementTargets';
import {
  dollarsCompact, dollarsFull, attainmentHex,
  CHART_BLUE, CHART_EMERALD, CHART_GRID, CHART_AXIS,
} from './LodgementDisplay';

/**
 * The team chart strip, the in-app answer to the workbook's quarterly Main
 * sheets.
 *
 * The workbook drew these as 3D pies. They are drawn here as bars on purpose:
 * the numbers are the same, but a pie asks the reader to compare angles, and
 * a 3D one distorts those angles by perspective, so the one thing these charts
 * exist for (ranking people against each other and against their target) is
 * the thing a 3D pie is worst at. Same data, legible.
 *
 * EVERY NUMBER COMES FROM THE COMPLETE PERIOD, not a page of the register, and
 * from the same rows the summary table below sums. A chart that disagreed with
 * the table under it would discredit both.
 *
 * ONE SCALE FOR ATTAINMENT. Bar colours come from `attainmentHex`, the same
 * 100/70 breakpoints behind every chip on this feature.
 */

interface Props {
  /** Every row in the window. */
  rows: LodgementRecord[];
  ownerLabels: Map<string, string>;
  progress: TargetProgress[];
  periodLabel: string;
  loading: boolean;
}

/** Which target types the percent chart will draw, when a person holds one. */
const PERCENT_TYPES: LodgementTargetType[] = ['lodgement_count', 'settlement_amount'];

/** Short suffixes, so an axis category stays readable at eight people. */
const PERCENT_SUFFIX: Record<string, string> = {
  lodgement_count: 'deals',
  settlement_amount: 'settled $',
  lodgement_amount: 'lodged $',
  settlement_count: 'settled deals',
};

function ChartCard({
  title, subtitle, color, children,
}: {
  title: string; subtitle?: string; color: string; children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="relative overflow-hidden rounded-lg bg-gray-800 p-6 shadow-lg">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}40 0%, transparent 70%)` }}
      />
      <div className="relative z-10 mb-4">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        {subtitle && <p className="mt-0.5 text-[11px] text-gray-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function TooltipBox({ label, lines }: { label: string; lines: Array<[string, string, string]> }) {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 p-4 text-white shadow-xl">
      <p className="mb-2 font-bold">{label}</p>
      {lines.map(([name, value, colour]) => (
        <p key={name} style={{ color: colour }}>
          {name}: {value}
        </p>
      ))}
    </div>
  );
}

export function LodgementTeamCharts({
  rows, ownerLabels, progress, periodLabel, loading,
}: Props): React.ReactElement | null {
  /** One entry per person with activity, ordered by value lodged. */
  const people = useMemo(() => {
    const byOwner = new Map<string, { lodgedCents: number; settledCents: number; count: number }>();
    for (const r of rows) {
      const key = r.ownerId || 'unattributed';
      const acc = byOwner.get(key) ?? { lodgedCents: 0, settledCents: 0, count: 0 };
      acc.count += 1;
      acc.lodgedCents += r.lodgeAmountCents || 0;
      // Same definition the summary table uses, so the two agree by construction.
      if (r.settledDate !== null || r.status === 'Settled') {
        acc.settledCents += r.settledAmountCents || 0;
      }
      byOwner.set(key, acc);
    }
    return Array.from(byOwner.entries())
      .map(([ownerId, v]) => ({
        ownerId,
        name: ownerLabels.get(ownerId) ?? ownerId,
        deals: v.count,
        lodged: v.lodgedCents / 100,
        settled: v.settledCents / 100,
      }))
      .sort((a, b) => b.lodged - a.lodged);
  }, [rows, ownerLabels]);

  const byDeals = useMemo(() => [...people].sort((a, b) => b.deals - a.deals), [people]);

  /**
   * One bar per person per target type they actually hold. Built from the
   * target records rather than from a fixed list of people, so a person with no
   * target simply has no bar instead of a misleading 0%.
   */
  const attainment = useMemo(
    () =>
      progress
        .filter((p) => !p.notApplicable && PERCENT_TYPES.includes(p.type))
        .map((p) => ({
          label: `${ownerLabels.get(p.ownerId) ?? p.ownerId} (${PERCENT_SUFFIX[p.type] ?? TARGET_TYPE_LABELS[p.type]})`,
          percent: p.percent,
          type: p.type,
          fill: attainmentHex(p.percent),
        }))
        .sort((a, b) => b.percent - a.percent),
    [progress, ownerLabels]
  );

  const totals = useMemo(
    () => ({
      deals: people.reduce((a, p) => a + p.deals, 0),
      lodged: people.reduce((a, p) => a + p.lodged, 0),
      settled: people.reduce((a, p) => a + p.settled, 0),
    }),
    [people]
  );

  if (!loading && people.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Team, {periodLabel}</h2>
          <p className="mt-0.5 text-[11px] text-gray-400">
            Everyone with activity in the period, from the same rows the table below sums.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[
            ['Total deals', String(totals.deals)],
            ['Total $ lodged', dollarsFull(totals.lodged)],
            ['Total $ settled', dollarsFull(totals.settled)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-gray-700 bg-gray-800/60 px-3 py-2">
              <div className="text-lg font-bold leading-none text-white">{value}</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartCard
          title="Lodged vs settled by person"
          subtitle="Dollars lodged in the period against dollars settled"
          color={CHART_BLUE}
        >
          <ResponsiveContainer width="100%" height={Math.max(280, people.length * 46)}>
            <BarChart data={people} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.3} />
              <XAxis dataKey="name" stroke={CHART_AXIS} fontSize={11} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis stroke={CHART_AXIS} fontSize={12} tickFormatter={dollarsCompact} />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <TooltipBox
                      label={String(label)}
                      lines={[
                        ['Lodged', dollarsFull(payload[0].payload.lodged), CHART_BLUE],
                        ['Settled', dollarsFull(payload[0].payload.settled), CHART_EMERALD],
                        ['Deals', String(payload[0].payload.deals), CHART_AXIS],
                      ]}
                    />
                  ) : null
                }
              />
              <Legend wrapperStyle={{ fontSize: 12, color: CHART_AXIS }} />
              <Bar dataKey="lodged" name="Lodged" fill={CHART_BLUE} radius={[4, 4, 0, 0]} animationDuration={800} />
              <Bar dataKey="settled" name="Settled" fill={CHART_EMERALD} radius={[4, 4, 0, 0]} animationDuration={800} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Lodged deals by person"
          subtitle="Count of lodgements in the period"
          color={CHART_EMERALD}
        >
          <ResponsiveContainer width="100%" height={Math.max(280, byDeals.length * 46)}>
            <BarChart data={byDeals} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.3} />
              <XAxis type="number" stroke={CHART_AXIS} fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke={CHART_AXIS} fontSize={12} width={130} />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <TooltipBox
                      label={String(label)}
                      lines={[
                        ['Lodgements', String(payload[0].payload.deals), CHART_EMERALD],
                        ['Value lodged', dollarsFull(payload[0].payload.lodged), CHART_AXIS],
                      ]}
                    />
                  ) : null
                }
              />
              <Bar
                dataKey="deals"
                fill={CHART_EMERALD}
                radius={[0, 4, 4, 0]}
                animationDuration={800}
                label={({ x, y, width, value }) =>
                  Number.isFinite(value) && value > 0 ? (
                    <text x={x + width + 6} y={y + 14} fill="#ffffff" fontSize={11} fontWeight="500">
                      {value}
                    </text>
                  ) : null
                }
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {attainment.length > 0 && (
        <ChartCard
          title="Percent of target by person"
          subtitle="One bar per target somebody actually holds for this period. The dashed line is 100%."
          color={CHART_EMERALD}
        >
          <ResponsiveContainer width="100%" height={Math.max(280, attainment.length * 40)}>
            <BarChart data={attainment} layout="vertical" margin={{ top: 5, right: 70, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.3} />
              <XAxis
                type="number"
                stroke={CHART_AXIS}
                fontSize={12}
                tickFormatter={(v) => `${v}%`}
                // Always show the 100% line even when nobody is close to it, so
                // the gap is visible rather than cropped out of frame.
                domain={[0, (max: number) => Math.max(110, Math.ceil(max / 10) * 10)]}
              />
              <YAxis type="category" dataKey="label" stroke={CHART_AXIS} fontSize={11} width={190} />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <TooltipBox
                      label={String(label)}
                      lines={[[
                        TARGET_TYPE_LABELS[payload[0].payload.type],
                        `${payload[0].payload.percent}% of target`,
                        payload[0].payload.fill,
                      ]]}
                    />
                  ) : null
                }
              />
              <ReferenceLine
                x={100}
                stroke="#ffffff"
                strokeDasharray="4 4"
                strokeOpacity={0.6}
                label={{ value: 'target', position: 'top', fill: '#ffffff', fontSize: 11 }}
              />
              <Bar
                dataKey="percent"
                radius={[0, 4, 4, 0]}
                animationDuration={800}
                label={({ x, y, width, value }) =>
                  Number.isFinite(value) ? (
                    <text x={x + width + 6} y={y + 14} fill="#ffffff" fontSize={11} fontWeight="500">
                      {value}%
                    </text>
                  ) : null
                }
              >
                {attainment.map((a) => (
                  <Cell key={a.label} fill={a.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}
